import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Check, X, Sparkles, ArrowRight, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Preis-Grid mit 3 Tiers (Team Flex / Pro Betrieb / Enterprise).
 * Wenn eine Digistore-Produkt-ID fehlt, oeffnet sich ein elegantes
 * Anfrage-Modal statt eines mailto-Links.
 */
export const PricingTiersGrid = ({ className = '', anchorId = 'preise' }) => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalTier, setModalTier] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/pricing/tiers`);
        const data = await res.json();
        if (data.success) setTiers(data.tiers || []);
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, []);

  const openCheckout = (tier) => {
    const url = tier.checkout_url;
    if (url) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    // Kein Digistore-Produkt hinterlegt -> Anfrage-Modal
    setModalTier(tier);
    setSent(false);
    setForm({ name: '', email: '', company: '', message: '' });
  };

  const submitInquiry = async () => {
    if (!form.email.trim() || !form.name.trim()) {
      toast.error('Bitte Name und E-Mail eingeben');
      return;
    }
    setSending(true);
    try {
      // Wir nutzen den Wunsch-Endpoint als schlanken Weg zur Sales-Nachricht.
      const text = `[Angebotsanfrage ${modalTier.name} · ${modalTier.price_monthly_eur}€/M]\n${form.message || 'Keine Zusatznachricht'}\nBetrieb: ${form.company || '—'}`;
      const res = await fetch(`${API}/api/training-wishes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          requester_name: form.name,
          requester_email: form.email,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Konnte Anfrage nicht senden');
      setSent(true);
      toast.success('Anfrage abgeschickt', { description: 'Wir melden uns innerhalb von 24 Stunden.' });
    } catch (err) {
      toast.error(err.message || 'Fehler');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id={anchorId} className={`py-14 bg-gradient-to-b from-black to-gray-950 ${className}`} data-testid="pricing-grid">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-xs uppercase tracking-widest mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Preise
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Ein Preis pro Betriebsgröße — nicht pro Mitarbeiter</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Keine überraschenden Zusatzkosten. Du wählst nach Betriebsgröße, alle Mitarbeiter sind drin, das komplette Kurs-Portfolio inklusive.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-6xl mx-auto">
          {loading ? [1,2,3].map((i) => (
            <Card key={i} className="bg-gray-900/50 border-gray-800 animate-pulse h-96" />
          )) : tiers.map((t) => (
            <Card
              key={t.key}
              className={`relative bg-gradient-to-b ${t.highlight ? 'from-yellow-500/20 to-transparent border-yellow-500/60 shadow-2xl shadow-yellow-500/20 md:scale-105' : 'from-gray-900/70 to-black border-gray-800'} border transition-all hover:border-yellow-500/40`}
              data-testid={`tier-card-${t.key}`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-yellow-500 text-black text-[11px] font-bold uppercase tracking-widest">
                  Am beliebtesten
                </div>
              )}
              <CardContent className="p-6">
                <h3 className="text-2xl font-bold text-white mb-1">{t.name}</h3>
                <p className="text-sm text-gray-400 mb-5 leading-snug">{t.subtitle}</p>

                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-5xl font-black text-white">{t.price_monthly_eur}</span>
                  <span className="text-gray-400">€</span>
                  <span className="text-gray-500 text-sm">/ Monat</span>
                </div>
                <p className="text-xs text-yellow-300/80 mb-6">
                  {t.seats_min}{t.seats_max ? `–${t.seats_max}` : '+'} Mitarbeiter · jährlich {t.price_yearly_eur} € (2 Monate geschenkt)
                </p>

                <Button
                  onClick={() => openCheckout(t)}
                  className={`w-full mb-2 ${t.highlight ? 'bg-yellow-500 hover:bg-yellow-400 text-black' : 'bg-gray-800 hover:bg-gray-700 text-white border border-yellow-500/30'} font-semibold`}
                  data-testid={`tier-cta-${t.key}`}
                >
                  {t.checkout_url ? t.cta : 'Angebot anfordern'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                {/* Digistore24-Pflichthinweis unter jedem CTA */}
                <p className="text-[10px] text-blue-300/90 text-center mb-5 leading-snug" data-testid={`tier-delivery-note-${t.key}`}>
                  100 % Digitaler Sofort-Zugang · Zugangsdaten direkt per E-Mail
                </p>

                <ul className="space-y-2">
                  {(t.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                      <span className="text-gray-200">{f}</span>
                    </li>
                  ))}
                  {(t.not_included || []).map((f, i) => (
                    <li key={`x${i}`} className="flex items-start gap-2 text-sm">
                      <X className="w-4 h-4 text-gray-600 shrink-0 mt-0.5" />
                      <span className="text-gray-600 line-through">{f}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        <p className="text-center text-xs text-gray-500 mt-8">
          Alle Preise netto zzgl. MwSt. · Monatlich kündbar zum Laufzeitende · Zahlung sicher über Digistore24
        </p>

        {/* Digistore24-Pflichtblock: enthaltene Inhalte + Lieferart im Kaufkontext */}
        <div
          className="mt-6 max-w-4xl mx-auto rounded-xl border border-yellow-500/30 bg-gradient-to-br from-yellow-500/[0.08] to-transparent p-5"
          data-testid="pricing-scope-notice"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
            <div>
              <p className="text-yellow-400 text-[11px] font-black uppercase tracking-widest mb-2">Enthalten in allen Paketen</p>
              <p className="text-gray-200 leading-relaxed">
                Vollzugriff auf rechtssichere Arbeits- &amp; Arbeitsschutz-Unterweisungen: <strong>BetrSichV</strong>, <strong>DGUV Vorschrift 1</strong>, <strong>VDE 0105-100</strong>, <strong>Leitern &amp; Gerüste</strong>, <strong>Baustellen-Recht</strong>, interaktive Wissens-Checks und automatische PDF-Zertifikate.
              </p>
            </div>
            <div>
              <p className="text-blue-300 text-[11px] font-black uppercase tracking-widest mb-2">Lieferart · Bildungsgut</p>
              <p className="text-gray-200 leading-relaxed">
                <strong>100 % Digitaler Sofort-Zugang</strong> — Nach dem Kauf erhalten Sie direkt Ihre Zugangsdaten per E-Mail für das Online-Portal. Die Abbuchung erfolgt durch Digistore24.com.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Anfrage-Modal fuer Tiers ohne Digistore-Produkt-ID */}
      {modalTier && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setModalTier(null)}
          data-testid="tier-inquiry-modal"
        >
          <div
            className="bg-gray-950 border border-yellow-500/40 rounded-2xl max-w-md w-full p-6 relative shadow-2xl shadow-yellow-500/10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setModalTier(null)}
              className="absolute top-3 right-3 text-gray-500 hover:text-white"
              data-testid="tier-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
            {sent ? (
              <div className="text-center py-6">
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
                  <Check className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="text-white font-bold text-xl mb-2">Anfrage abgeschickt</h3>
                <p className="text-gray-400 text-sm mb-5">
                  Danke für Dein Interesse an <strong className="text-yellow-300">{modalTier.name}</strong>.<br />
                  Wir melden uns innerhalb von 24 Stunden mit einem persönlichen Zugang.
                </p>
                <Button onClick={() => setModalTier(null)} className="bg-yellow-500 hover:bg-yellow-400 text-black">
                  Schließen
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">
                      {modalTier.name} — Zugang anfordern
                    </h3>
                    <p className="text-xs text-gray-400">
                      {modalTier.price_monthly_eur} €/Monat · {modalTier.seats_min}{modalTier.seats_max ? `–${modalTier.seats_max}` : '+'} Mitarbeiter
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-400 mb-4">
                  Dieses Tier ist gerade auf persönliche Betreuung eingerichtet. Wir schalten Dich innerhalb 24 h frei.
                </p>
                <div className="space-y-2">
                  <Input
                    placeholder="Dein Name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-black/50 border-gray-700"
                    data-testid="tier-modal-name"
                  />
                  <Input
                    type="email"
                    placeholder="E-Mail"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-black/50 border-gray-700"
                    data-testid="tier-modal-email"
                  />
                  <Input
                    placeholder="Betrieb (optional)"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="bg-black/50 border-gray-700"
                  />
                  <Textarea
                    placeholder="Zusatzinfo (optional): Anzahl Mitarbeiter, Wunsch-Termin, …"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="bg-black/50 border-gray-700 min-h-[70px]"
                  />
                </div>
                <Button
                  onClick={submitInquiry}
                  disabled={sending}
                  className="w-full mt-4 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold"
                  data-testid="tier-modal-submit"
                >
                  {sending ? (
                    <>Sende…</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Angebot anfordern</>
                  )}
                </Button>
                <p className="text-[10px] text-gray-500 text-center mt-3">
                  Alternative: Direkt an info@smartvoltacademy.de schreiben.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default PricingTiersGrid;
